import { useEffect, useState } from "react";

interface UseMarkdownContentResult {
  content: string;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch and manage markdown content from a URL.
 * Handles loading state, errors, and cleanup on unmount.
 *
 * @param markdownUrl - The URL to fetch markdown content from
 * @returns Object containing content, loading state, and error
 */
export function useMarkdownContent(
  markdownUrl: string
): UseMarkdownContentResult {
  const [content, setContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchContent = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(markdownUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch markdown: ${response.statusText}`);
        }
        const text = await response.text();

        if (isMounted) {
          setContent(text);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error ? err : new Error("Failed to load content")
          );
          setIsLoading(false);
        }
      }
    };

    if (markdownUrl) {
      fetchContent();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [markdownUrl]);

  return { content, isLoading, error };
}
